import { useEffect, useState } from 'react';
import { X, RefreshCw, Flame, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { ScoreBadge } from './ScoreBadge';

interface ScoreFactor {
  name: string;
  points: number;
  maxPoints: number;
  reason: string;
}

interface BreakdownResponse {
  score: number;
  tier: 'hot' | 'warm' | 'cold' | 'frozen';
  tierLabel: string;
  tierColor: string;
  factors: ScoreFactor[];
}

interface Props {
  leadId: string;
  leadName: string;
  open: boolean;
  onClose: () => void;
  onScoreUpdated?: (newScore: number) => void;
}

/**
 * Right-side drawer showing how the lead's score is computed.
 * Also exposes a "Recalculate" button that re-runs the scoring service
 * server-side and updates the badge instantly. The backend's
 * /score-breakdown endpoint already recalculates on every call, so just
 * re-fetching is enough.
 */
export function ScoreBreakdownDrawer({ leadId, leadName, open, onClose, onScoreUpdated }: Props) {
  const [data, setData] = useState<BreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const fetchBreakdown = async (showLoader: boolean) => {
    if (showLoader) setLoading(true);
    try {
      const { data: res } = await api.get<BreakdownResponse>(`/leads/${leadId}/score-breakdown`);
      setData(res);
      onScoreUpdated?.(res.score);
    } catch {
      toast('error', 'Failed to load score breakdown');
    } finally {
      setLoading(false);
      setRecalculating(false);
    }
  };

  useEffect(() => {
    if (open) fetchBreakdown(true);
    // intentional: leadId/open are the only inputs that should refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, open]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    await fetchBreakdown(false);
    toast('success', 'Score recalculated');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Flame className="h-5 w-5 text-[#6366F1]" />
              Score Breakdown
            </h2>
            <p className="text-xs text-slate-500 truncate">{leadName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-y-auto">
            {/* Score summary */}
            <div className="px-6 py-5 border-b border-slate-100 text-center">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full mb-2"
                style={{ backgroundColor: `${data.tierColor}15`, border: `3px solid ${data.tierColor}` }}>
                <span className="text-2xl font-bold" style={{ color: data.tierColor }}>{data.score}</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">
                {data.tierLabel} <span className="text-slate-400 font-normal">· {data.score}/100</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Auto-recalculates when status, source, or contact info changes.
              </p>
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#6366F1] hover:text-[#4F46E5] disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${recalculating ? 'animate-spin' : ''}`} />
                Recalculate now
              </button>
            </div>

            {/* Factors */}
            <div className="px-6 py-4 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Contributing Factors
              </p>
              {data.factors.map((f, i) => (
                <FactorRow key={i} factor={f} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
            No data
          </div>
        )}
      </div>
    </div>
  );
}

function FactorRow({ factor }: { factor: ScoreFactor }) {
  const pct = factor.maxPoints > 0 ? (factor.points / factor.maxPoints) * 100 : 0;
  const tone =
    pct >= 80 ? 'bg-emerald-500' :
    pct >= 40 ? 'bg-amber-500' :
    pct > 0   ? 'bg-blue-400' :
                 'bg-slate-300';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-medium text-slate-700">{factor.name}</p>
        <p className="text-xs font-semibold text-slate-600">
          +{factor.points} <span className="text-slate-400 font-normal">/ {factor.maxPoints}</span>
        </p>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-slate-400 mt-0.5">{factor.reason}</p>
    </div>
  );
}
